import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  Icon,
  IconButton,
  Typography,
  Modal,
  Box,
  TextField,
  Button,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import Swal from "sweetalert2";
import Loader from "examples/Loader";

const DEFAULT_COLOR = "#3B82F6";

const newId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const CategoriesTab = () => {
  const AppService = useAppServices();
  const [agency, setAgencyUpdate] = useAgencyInfo();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState({
    id: null,
    name: "",
  });

  useEffect(() => {
    if (Array.isArray(agency?.Categories)) {
      let data = [...agency.Categories];
      // Ensure "Other" is always present if needed, though ideally it should be in the DB
      const hasOther = data.some(item => item.name.toLowerCase() === 'other');
      if (!hasOther) {
        data.push({ name: 'Other', id: 'other-id', isStatic: true });
      }
      setCategories(data);
    } else {
      setCategories([{ name: 'Other', id: 'other-id', isStatic: true }]);
    }
  }, [agency]);

  const openCreate = () => {
    setModalData({ id: null, name: "" });
    setOpenModal(true);
  };

  const openEdit = (category) => {
    setModalData({
      id: category.id,
      name: category.name,
    });
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setModalData({ id: null, name: "" });
  };

  const persistCategories = async (updatedCategories) => {
    setLoading(true);
    try {
      const { response } = await AppService.agency.update({
        payload: { _id: agency._id, Categories: updatedCategories },
        toaster: true,
      });
      if (response?.success) {
        await AppService.agency.updateCustomFieldOptions({
          payload: { agency_id: agency._id, options: [...updatedCategories?.map((item) => item.name)], field_id: agency?.custom_fields?.category?.split('-')?.[0] },
          toaster: true
        });
        setAgencyUpdate(response.data);
      }
    } catch (error) {
      console.error("Error saving category:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const name = modalData.name.trim();
    if (!name) return;

    const isDuplicate = categories.some(
      (category) =>
        category.name.trim().toLowerCase() === name.toLowerCase() &&
        category.id !== modalData.id,
    );
    if (isDuplicate) {
      Swal.fire("Duplicate category", "A category with this name already exists.", "warning");
      return;
    }

    const trimmed = {
      ...modalData,
      name
    };

    const updated = trimmed.id
      ? categories.map((category) =>
        category.id === trimmed.id ? { ...category, ...trimmed } : category,
      )
      : [...categories, { ...trimmed, id: newId() }];

    await persistCategories(updated);
    closeModal();
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#344767",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (!result.isConfirmed) return;
      persistCategories(categories.filter((category) => category.id !== id));
    });
  };

  if (loading) return <Loader />;

  return (
    <SoftBox p={3}>
      <Grid container style={{ gap: "8px" }}>
        {categories.map((category) => {
          const isOther = category.name.toLowerCase() === 'other';
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
              <Card
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  border: "1px solid #ebebeb",
                  borderRadius: "12px",
                  boxShadow: "none",
                  minHeight: "60px",
                  paddingLeft: "10px",
                }}
              >
                <SoftBox display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <Typography
                    variant="button"
                    fontWeight="medium"
                    sx={{
                      color: "#344767",
                      textTransform: "none",
                      fontSize: "14px",
                    }}
                  >
                    {category.name}
                  </Typography>
                </SoftBox>
                {!isOther && (
                  <SoftBox display="flex" alignItems="center" sx={{ gap: 1 }}>
                    <IconButton size="small" onClick={() => openEdit(category)}>
                      <Icon sx={{ color: "#000", fontSize: "18px !important" }}>
                        edit
                      </Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(category.id)}
                    >
                      <Icon sx={{ color: "#f44336", fontSize: "18px !important" }}>
                        delete
                      </Icon>
                    </IconButton>
                  </SoftBox>
                )}
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <SoftBox mt={4}>
        <Button
          variant="outlined"
          onClick={openCreate}
          sx={{
            border: "1px solid #ebebeb",
            color: "#000",
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            py: 1,
            fontSize: "14px",
            fontWeight: "bold",
            "&:hover": {
              border: "1px solid #000",
              backgroundColor: "transparent",
            },
          }}
        >
          + Add New Category
        </Button>
      </SoftBox>

      <Modal open={openModal} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            bgcolor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            p: 4,
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" fontWeight="bold">
              {modalData.id ? "Edit category" : "Create new category"}
            </Typography>
            <Icon sx={{ cursor: "pointer", color: "#000" }} onClick={closeModal}>
              close
            </Icon>
          </SoftBox>

          <TextField
            fullWidth
            placeholder="Category name"
            value={modalData.name}
            onChange={(event) =>
              setModalData({ ...modalData, name: event.target.value })
            }
            variant="outlined"
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={loading || !modalData.name.trim()}
            sx={{
              backgroundColor: "#000 !important",
              color: "#fff !important",
              borderRadius: "8px",
              py: 1.5,
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Modal>
    </SoftBox>
  );
};

export default CategoriesTab;
